import { Drawer, message } from 'antd';
import ClientsSelect from 'components/ClientsSelect';
import { PaymentInfoTables } from 'components/PaymentsInfoTables';
import { FETCH_ERROR } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { useEffect, useState } from 'react';
import { PaymentsService } from 'services/PaymentsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { CardReversalPaymentsTable } from './CardReversalPaymentsTable';

const PAGE_TITLE = 'Возвратные платежи';

function CardReversalPayments() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [payments, setCardReversalPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const tribe_id = useTribe();
  const initialPaymentsInfo = {
    cardPayments: null,
    clientsPayments: null,
    cardReversalPayments: null,
  };
  const [paymentsInfo, setPaymentsInfo] = useState(initialPaymentsInfo);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    status: null,
    period: null,
  });
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const {
    state: { drawerVisible, activeRecord },
    openDrawer,
    closeDrawer,
  } = useDrawers();

  useEffect(() => {
    fetchCardReversalPayments();
  }, [page, perPage, filters]);

  useEffect(() => {
    if (activeRecord) {
      fetchPaymentsInfo();
    }
  }, [activeRecord]);

  const fetchCardReversalPayments = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await PaymentsService.getCardReversalPayments(
        page,
        filters,
        tribe_id,
        perPage
      );

      setCardReversalPayments(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsInfo = async () => {
    setLoading(true);

    try {
      const { card_payment, payment, card_reversal } =
        await PaymentsService.getCardReversalPaymentsDetail(activeRecord?.id);

      setPaymentsInfo({
        cardPayments: card_payment,
        clientsPayments: payment,
        cardReversalPayments: card_reversal,
      });
    } catch (err) {
      setPaymentsInfo(initialPaymentsInfo);
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />
      </ActionsWrapper>

      <Drawer
        title={activeRecord?.company_name}
        width="70%"
        onClose={closeDrawer}
        visible={drawerVisible}
        getContainer={false}
      >
        <PaymentInfoTables loading={loading} payments={paymentsInfo} />
      </Drawer>

      <CardReversalPaymentsTable
        loading={loading}
        items={payments}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />
    </>
  );
}

export { CardReversalPayments };
